import { ReactNode, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { ApiLogEntry } from "../lib/types";
import { forgetEverything } from "../lib/session";
import EnvSwitcher from "./EnvSwitcher";
import KeyInput from "./KeyInput";
import DryRunToggle from "./DryRunToggle";
import RequestLog from "./RequestLog";

type Props = {
  children: ReactNode;
  log: ApiLogEntry[];
  onClearLog: () => void;
};

export default function Layout({ children, log, onClearLog }: Props) {
  const [logOpen, setLogOpen] = useState(true);

  function handleForget() {
    if (!confirm("Clear your API key and environment from this tab?")) return;
    forgetEverything();
    location.reload();
  }

  return (
    <div className="h-screen flex flex-col">
      <header className="bg-white border-b border-slate-200 flex-shrink-0">
        <div className="flex items-center gap-4 px-4 py-2 flex-wrap">
          <Link to="/" className="font-semibold text-brand-700 whitespace-nowrap">
            HPAC API Explorer
          </Link>
          <EnvSwitcher />
          <KeyInput />
          <DryRunToggle />
          <button
            className="ml-auto text-sm text-red-600 hover:text-red-800 border border-red-200 rounded px-2 py-1"
            onClick={handleForget}
            title="Clear API key and environment from this tab"
          >
            Forget key
          </button>
        </div>
      </header>

      <div className="flex-1 flex min-h-0">
        <nav className="w-52 border-r border-slate-200 bg-white py-4 hidden md:block">
          <div className="px-4 text-[10px] uppercase tracking-wider text-slate-400 mb-1">Data</div>
          <NavItem to="/registrants">Registrants</NavItem>
          <NavItem to="/cert-types">Cert types</NavItem>
          <div className="px-4 text-[10px] uppercase tracking-wider text-slate-400 mt-4 mb-1">Reference</div>
          <NavItem to="/docs">Documentation</NavItem>
        </nav>

        <main className="flex-1 overflow-auto p-6">{children}</main>
      </div>

      <div className="flex-shrink-0">
        <RequestLog log={log} open={logOpen} onToggle={() => setLogOpen(v => !v)} onClear={onClearLog} />
      </div>
    </div>
  );
}

function NavItem({ to, children }: { to: string; children: ReactNode }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `block px-4 py-2 text-sm ${
          isActive ? "text-brand-700 bg-brand-50 border-l-2 border-brand-600" : "text-slate-700 hover:bg-slate-50"
        }`
      }
    >
      {children}
    </NavLink>
  );
}

